import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  CheckCircle as ApproveIcon,
  RocketLaunch as ActivateIcon,
  Refresh as ReplanIcon,
  NightsStay as CloseIcon,
  WbSunny as TomorrowIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { usePlanState } from '../hooks/usePlanState';
import { useAuth } from '../auth/AuthProvider';
import type { PlanStatus } from '../types/models';
import { planCopy } from '../copy/plan';

import StatusChip from '../components/StatusChip';
import NextActionCard from '../components/dashboard/NextActionCard';
import PriorityList from '../components/dashboard/PriorityList';
import RiskStrip from '../components/dashboard/RiskStrip';
import CapacityStrip from '../components/dashboard/CapacityStrip';
import WhyAffordance from '../components/ai/WhyAffordance';
import { buildRationaleMap } from '../utils/aiRationale';
import {
  deriveCapacity,
  deriveNextAction,
  derivePriorities,
  deriveRisks,
} from '../utils/dashboard';

// ---------------------------------------------------------------------------
// UnifiedPlanPage — Phase 2 task 2.1.
//
// One screen that renders the DailyPlan differently based on its status. The
// lifecycle is Draft → Proposed → Approved → Active → Adjusted → Closed, with
// EA and Executive seeing different actions (EA can send for approval and
// replan; only the Executive approves, activates, and closes).
//
// All data loading and transitions are delegated to `usePlanState(date)` so
// the rendering code here stays status-driven and side-effect-free.
// ---------------------------------------------------------------------------

function explainerFor(status: PlanStatus | null): string | null {
  switch (status) {
    case 'Draft':
      return planCopy.explainDraft;
    case 'Proposed':
      return planCopy.explainProposed;
    case 'Approved':
      return planCopy.explainApproved;
    case 'Active':
      return planCopy.explainActive;
    case 'Adjusted':
      return planCopy.explainAdjusted;
    case 'Closed':
      return planCopy.explainClosed;
    default:
      return null;
  }
}

export default function UnifiedPlanPage() {
  const { user } = useAuth();
  const isEa = user?.role === 'ExecutiveAssistant';

  const today = format(new Date(), 'yyyy-MM-dd');
  const prettyDate = format(new Date(), 'EEEE d MMMM');

  const {
    data,
    plan,
    status,
    initialLoading,
    busy,
    error,
    closeResult,
    canApprove,
    canActivate,
    canReplan,
    canClose,
    start,
    replan,
    approve,
    activate,
    close,
    planTomorrow,
    clearError,
    clearCloseResult,
  } = usePlanState(today);

  const [briefingOpen, setBriefingOpen] = useState(true);
  const [reflectionContent, setReflectionContent] = useState('');
  const [reflectionInsights, setReflectionInsights] = useState('');
  const [reflectionAdjustments, setReflectionAdjustments] = useState('');

  // ---- Derivations ------------------------------------------------------
  const nextAction = useMemo(() => deriveNextAction(data), [data]);
  const priorities = useMemo(() => derivePriorities(data, 3), [data]);
  const risks = useMemo(() => deriveRisks(data), [data]);
  const capacityLine = useMemo(
    () => (data ? deriveCapacity(data.capacity) : null),
    [data],
  );
  const rationaleMap = useMemo(() => buildRationaleMap(data), [data]);
  const nextActionRationale = nextAction?.workItemId
    ? rationaleMap.get(nextAction.workItemId)
    : undefined;

  const handleClose = async () => {
    await close({
      reflectionContent: reflectionContent.trim() || undefined,
      insights: reflectionInsights.trim() || undefined,
      adjustmentsMade: reflectionAdjustments.trim() || undefined,
    });
  };

  const hasAnyPlan = plan !== null;
  const hasFullData = data !== null;
  const explainer = explainerFor(status);

  // ---- Render -----------------------------------------------------------
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header row: date + status chip + role-appropriate transition actions. */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}
            >
              {prettyDate}
            </Typography>
            {status && <StatusChip status={status} />}
            <WhyAffordance
              rationale={plan?.aiRationale}
              secondary={data?.aiStrategyAlignment}
              label="Why this shape of day"
            />
          </Stack>
          {plan?.dailyTheme && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              {plan.dailyTheme}
            </Typography>
          )}
        </Box>

        <ActionBar
          status={status}
          hasAnyPlan={hasAnyPlan}
          hasFullData={hasFullData}
          isEa={isEa}
          busy={busy}
          canApprove={canApprove}
          canActivate={canActivate}
          canReplan={canReplan}
          canClose={canClose}
          onStart={start}
          onReplan={replan}
          onApprove={approve}
          onActivate={activate}
          onPlanTomorrow={planTomorrow}
        />
      </Stack>

      {explainer && !initialLoading && (
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', mb: 3 }}
        >
          {explainer}
        </Typography>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}
      {closeResult && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={clearCloseResult}>
          Day closed. {closeResult.itemsToCarryForward}{' '}
          {closeResult.itemsToCarryForward === 1 ? 'item' : 'items'} carried forward.
        </Alert>
      )}

      {/* Handoff notes from EA — surface prominently for the executive on a
          Proposed plan. */}
      {status === 'Proposed' && plan?.handoffNotes && (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 3, borderRadius: 2, borderColor: 'primary.main' }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {planCopy.handoffNotesLabel}
            {plan.preparedBy ? ` — ${plan.preparedBy}` : ''}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
            {plan.handoffNotes}
          </Typography>
        </Paper>
      )}

      {/* Status-driven body */}
      {initialLoading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !hasAnyPlan ? (
        <EmptyPlanState busy={busy} onStart={start} />
      ) : status === 'Closed' ? (
        <ClosedSummary plan={plan} closeResult={closeResult} />
      ) : !hasFullData ? (
        <PlanSummaryCard
          theme={plan?.dailyTheme}
          briefing={plan?.aiBriefing}
          risks={plan?.aiRisks}
          busy={busy}
          onLoadFull={start}
        />
      ) : (
        <PlanBody
          nextAction={nextAction}
          priorities={priorities}
          risks={risks}
          capacity={capacityLine}
          briefing={data?.aiBriefing ?? plan?.aiBriefing ?? null}
          aiRisks={data?.aiRisks ?? plan?.aiRisks ?? null}
          briefingOpen={briefingOpen}
          onToggleBriefing={() => setBriefingOpen((v) => !v)}
          rationaleMap={rationaleMap}
          nextActionRationale={nextActionRationale?.rationale}
          nextActionStrategyLink={nextActionRationale?.strategyLink}
        />
      )}

      {/* Reflection capture shown only when closing is in reach (Active /
          Adjusted), so the executive can jot a few lines alongside the Close
          day action instead of being sent to /reflections. */}
      {(status === 'Active' || status === 'Adjusted') && hasFullData && (
        <Paper variant="outlined" sx={{ p: 2.5, mt: 4, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {planCopy.closeDayHeading}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
            Optional — leave blank to close without a reflection.
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              label={planCopy.closeDayPromptWorked}
              size="small"
              multiline
              minRows={2}
              value={reflectionContent}
              onChange={(e) => setReflectionContent(e.target.value)}
            />
            <TextField
              label={planCopy.closeDayPromptSlipped}
              size="small"
              multiline
              minRows={2}
              value={reflectionInsights}
              onChange={(e) => setReflectionInsights(e.target.value)}
            />
            <TextField
              label={planCopy.closeDayPromptTomorrow}
              size="small"
              multiline
              minRows={2}
              value={reflectionAdjustments}
              onChange={(e) => setReflectionAdjustments(e.target.value)}
            />
            <Box>
              <Button
                variant="contained"
                startIcon={<CloseIcon />}
                onClick={handleClose}
                disabled={busy}
              >
                {planCopy.closeDayAction}
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Header action bar — the buttons visible depend on role + status. Keeping
// the logic here makes the main component's layout read top-down.
// ---------------------------------------------------------------------------

interface ActionBarProps {
  status: PlanStatus | null;
  hasAnyPlan: boolean;
  hasFullData: boolean;
  isEa: boolean;
  busy: boolean;
  canApprove: boolean;
  canActivate: boolean;
  canReplan: boolean;
  canClose: boolean;
  onStart: () => void;
  onReplan: () => void;
  onApprove: () => void;
  onActivate: () => void;
  onPlanTomorrow: () => void;
}

function ActionBar({
  status,
  hasAnyPlan,
  hasFullData,
  isEa,
  busy,
  canApprove,
  canActivate,
  canReplan,
  canClose,
  onStart,
  onReplan,
  onApprove,
  onActivate,
  onPlanTomorrow,
}: ActionBarProps) {
  if (!hasAnyPlan) {
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <StartIcon />}
          onClick={onStart}
          disabled={busy}
        >
          {planCopy.startMyDay}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {/* EA can send for approval on Draft; currently the backend emits Proposed
          directly, so this button is only shown if a Draft somehow exists. */}
      {isEa && status === 'Draft' && (
        <Button
          variant="contained"
          onClick={onStart}
          disabled={busy}
        >
          {planCopy.sendForApproval}
        </Button>
      )}

      {/* Executive-only: Approve on Proposed. EAs see a read-only hint. */}
      {canApprove && !isEa && (
        <Button
          variant="contained"
          startIcon={<ApproveIcon />}
          onClick={onApprove}
          disabled={busy}
        >
          {planCopy.approvePlan}
        </Button>
      )}

      {canActivate && !isEa && (
        <Button
          variant="contained"
          startIcon={<ActivateIcon />}
          onClick={onActivate}
          disabled={busy}
        >
          {planCopy.activatePlan}
        </Button>
      )}

      {canReplan && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<ReplanIcon />}
          onClick={onReplan}
          disabled={busy}
        >
          {status === 'Active' || status === 'Adjusted'
            ? planCopy.replanMidDay
            : planCopy.adjustPlan}
        </Button>
      )}

      {canClose && !isEa && hasFullData && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<CloseIcon />}
          onClick={() => {
            // Close flow handled via the reflection capture paper at the bottom
            // of the page. This button scrolls to it.
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
          }}
          disabled={busy}
        >
          {planCopy.closeDayAction}
        </Button>
      )}

      {status === 'Closed' && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<TomorrowIcon />}
          onClick={onPlanTomorrow}
          disabled={busy}
        >
          Plan tomorrow
        </Button>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Body variants
// ---------------------------------------------------------------------------

function EmptyPlanState({
  busy,
  onStart,
}: {
  busy: boolean;
  onStart: () => void;
}) {
  return (
    <Paper
      sx={{
        p: 5,
        textAlign: 'center',
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        {planCopy.noPlanHeading}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', mb: 3, maxWidth: 520, mx: 'auto' }}
      >
        {planCopy.noPlanBody}
      </Typography>
      <Button
        variant="contained"
        size="large"
        startIcon={<StartIcon />}
        onClick={onStart}
        disabled={busy}
      >
        {planCopy.startMyDay}
      </Button>
    </Paper>
  );
}

// Shown when the plan record exists but the full schedule hasn't been loaded
// yet (the usual case on a return visit mid-day).
function PlanSummaryCard({
  theme,
  briefing,
  risks,
  busy,
  onLoadFull,
}: {
  theme?: string;
  briefing?: string | null;
  risks?: string | null;
  busy: boolean;
  onLoadFull: () => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      {theme && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {planCopy.dailyThemeLabel}: {theme}
        </Typography>
      )}
      {briefing && (
        <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', mb: 2 }}>
          {briefing}
        </Typography>
      )}
      {risks && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main' }}>
            {planCopy.risksLabel}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
            {risks}
          </Typography>
        </Box>
      )}
      <Button
        variant="contained"
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ReplanIcon />}
        onClick={onLoadFull}
        disabled={busy}
      >
        Load current plan
      </Button>
    </Paper>
  );
}

function PlanBody({
  nextAction,
  priorities,
  risks,
  capacity,
  briefing,
  aiRisks,
  briefingOpen,
  onToggleBriefing,
  rationaleMap,
  nextActionRationale,
  nextActionStrategyLink,
}: {
  nextAction: ReturnType<typeof deriveNextAction>;
  priorities: ReturnType<typeof derivePriorities>;
  risks: ReturnType<typeof deriveRisks>;
  capacity: ReturnType<typeof deriveCapacity> | null;
  briefing: string | null;
  aiRisks: string | null;
  briefingOpen: boolean;
  onToggleBriefing: () => void;
  rationaleMap?: ReturnType<typeof buildRationaleMap>;
  nextActionRationale?: string;
  nextActionStrategyLink?: string;
}) {
  return (
    <Stack spacing={2}>
      <NextActionCard
        action={nextAction}
        rationale={nextActionRationale}
        rationaleStrategyLink={nextActionStrategyLink}
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
        <Box sx={{ flex: 2, minWidth: 0 }}>
          <PriorityList items={priorities} rationaleMap={rationaleMap} />
        </Box>
        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
          <RiskStrip risks={risks} aiRationale={aiRisks} />
          {capacity && <CapacityStrip capacity={capacity} />}
        </Stack>
      </Stack>

      {briefing && (
        <Box sx={{ mt: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ cursor: 'pointer', userSelect: 'none', py: 1 }}
            onClick={onToggleBriefing}
          >
            <Typography
              variant="overline"
              sx={{ color: 'text.secondary', letterSpacing: '0.12em', fontWeight: 600 }}
            >
              {planCopy.aiBriefingLabel}
            </Typography>
            <IconButton size="small" aria-label={briefingOpen ? 'Hide briefing' : 'Show briefing'}>
              {briefingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
          <Collapse in={briefingOpen} timeout={180} unmountOnExit>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}
              >
                {briefing}
              </Typography>
            </Paper>
          </Collapse>
        </Box>
      )}
    </Stack>
  );
}

function ClosedSummary({
  plan,
  closeResult,
}: {
  plan: ReturnType<typeof usePlanState>['plan'];
  closeResult: ReturnType<typeof usePlanState>['closeResult'];
}) {
  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Chip size="small" label={planCopy.stateClosed} />
        <Typography variant="subtitle2">{planCopy.explainClosed}</Typography>
      </Stack>
      {plan?.dailyTheme && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          {planCopy.dailyThemeLabel}: {plan.dailyTheme}
        </Typography>
      )}
      {plan?.aiBriefing && (
        <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', mb: 1.5 }}>
          {plan.aiBriefing}
        </Typography>
      )}
      {closeResult && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {closeResult.itemsToCarryForward}{' '}
          {closeResult.itemsToCarryForward === 1 ? 'item' : 'items'} queued for tomorrow.
        </Typography>
      )}
    </Paper>
  );
}
